import { isNull, isUndefined, set } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../../../hooks/react-redux';
import { capitaliseStartLetter } from '../../../../../utils/regex-utils';
import { getAllDropdownLists } from '../../../../admin/dropdown-lists/store/selectors';
import { initialCurrencyFilter } from '../../../../constants/instanceFilters';
import { Dropdown, DropdownOption } from '../../../../shared/dropdown/Dropdown';
import { Comparator, CurrencyFilterType } from '../../store/filterTypes';
import { ComparatorFilter } from './ComparatorFilter';
import { SharedNumberFilter } from './SharedNumberFilter';
import styles from './TableInstanceFilters.module.scss';

interface CurrencyFilterProps {
    updateFilter: (value: CurrencyFilterType) => void;
    removeFilter: () => void;
    filter: CurrencyFilterType | null;
    testId: string;
}

export const CurrencyFilter: React.FC<CurrencyFilterProps> = ({ updateFilter, removeFilter, filter, testId }) => {
    const currencyFilter = useMemo(() => isNull(filter) ? initialCurrencyFilter : filter, [filter]);
    const { comparator, value, currency } = currencyFilter;

    const allDropdownLists = useAppSelector(getAllDropdownLists);
    const currencyDropdownOptions = useMemo(() => (allDropdownLists.find(({ name }) => name === 'Currency Codes')?.options || []).map(option => ({ value: option, label: capitaliseStartLetter(option) })), [allDropdownLists]);

    const currencyFilterValue = useMemo(() => {
        if (currencyDropdownOptions.length > 0 && !isNull(currency)) {
            return currencyDropdownOptions.filter(({ value }) => currency.includes(value));
        }
        return null;
    }, [currencyDropdownOptions, currency]);

    const updateCurrencyFilter = useCallback((key: keyof CurrencyFilterType, value: number | Comparator | undefined | null | string[]) => {
        if ((key === 'value' && isUndefined(value) && isNull(currency) && isNull(comparator)) || (key === 'comparator' && isNull(value) && isUndefined(currencyFilter.value) && isNull(currency)) || (key === 'currency' && isNull(value) && isUndefined(currencyFilter.value) && isNull(comparator))) {
            removeFilter();
            return;
        }
        const updatedFilter = set(key, value, currencyFilter);
        updateFilter(updatedFilter);
    }, [removeFilter, updateFilter, currencyFilter, comparator, currency]);

    const updateComparatorFilter = useCallback((value: Comparator | null) => updateCurrencyFilter('comparator', value), [updateCurrencyFilter]);
    const updateCurrencyValueFilter = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let cityValue = null;
        if (!isNull(dropdownValue) && (dropdownValue as Options<DropdownOption>).length > 0) {
            cityValue = (dropdownValue as Options<DropdownOption>).map(({ value }) => value);
        }
        updateCurrencyFilter('currency', cityValue);
    }, [updateCurrencyFilter]);

    const updateNumberValue = useCallback((value: number | undefined) => updateCurrencyFilter('value', value), [updateCurrencyFilter]);

    return (
        <div className={styles.currencyFilterWrapper}>
            <div className={styles.currencyWrapper}>
                <div className={styles.verticalFilterLabel}>Currency</div>
                <Dropdown
                    value={currencyFilterValue}
                    onChange={updateCurrencyValueFilter}
                    options={currencyDropdownOptions}
                    testId={`${testId}-currency-filter`}
                    menuPlacement='bottom'
                    isClearable
                    menuPortalTarget={document.body}
                    isMulti
                />
            </div>
            <div className={styles.verticalFilterLabel}>Amount</div>
            <div className={styles.comparatorSplitWrapper}>
                <ComparatorFilter updateFilter={updateComparatorFilter} filter={comparator} testId={testId} />
                <SharedNumberFilter updateFilter={updateNumberValue} filter={value} testId={testId} placeholder='Amount...' />
            </div>
        </div>
    );
};
